const FORMATTED_ADDR_REGEX = /^.*\s.+,\s.+,\s.+\s.+$/;

// Not guaranteed this constant is used everywhere
const DELIVERY_RANGE = 5;

const DELIVERY_TIP_PERCENT = 0.15;
const DELIVERY_SERVICE_FEE_CENTS = 700;
const IN_HOUSE_SERVICE_FEE_CENTS = 99;
const SALES_TAX_PERCENT = 0.115; // Highest sales tax in the US according to https://www.avalara.com/taxrates/en/state-rates.html

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

// Takes an address string and returns an object to be set as addressDetails state value
const parseFormattedAddress = addr => {
  if (!FORMATTED_ADDR_REGEX.test(addr)) return { invalid: true };

  const split = addr.split(', ');
  const streetAddressAndApt = split[0];
  const city = split[1].trim();
  const [state, zip] = split[2]
    .trim()
    .split(/\s+/)
    .map(str => str.trim());

  return {
    streetAddress: streetAddressAndApt,
    apt: '',
    city,
    state,
    zip,
  };
};

/**
 * Get the location of an address using the Google Maps Geocoder
 * @param {google.maps.Geocoder} geocoder instance of Geocoder class to use
 * @param {string} address address to geocode
 * @returns {google.maps.LatLng | null} location of the address
 */
const getAddressLocation = async (geocoder, address) => {
  const resultPromise = new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (res, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK) {
        reject(Error(`Geocoding failed for ${address}: ${status}`));
      }

      resolve(res[0].geometry.location);
    });
  });

  try {
    return await resultPromise;
  } catch (e) {
    console.error(`Failed to get address location: ${e}`);
    return null;
  }
};

/**
 * Check if two locations are within a given range
 * @param {google.maps.LatLng} locationA Google Maps location object with a latitude and longitude
 * @param {google.maps.LatLng} locationB Google Maps location object with a latitude and longitude
 * @param {number} rangeMiles checked range in miles
 * @returns {boolean} True if locations are within rangeMiles of each other
 */
const areLocationsInRange = (locationA, locationB, rangeMiles) => {
  const distMeters = window.google.maps.geometry.spherical.computeDistanceBetween(locationA, locationB);
  const distMiles = distMeters / 1609.34;
  return distMiles < rangeMiles;
};

/**
 * Check if two addresses are within a given range using geocoder
 * @param {google.maps.Geocoder} geocoder instance of Geocoder class to use
 * @param {string} addressA first address line
 * @param {string} addressB second address line
 * @param {number} rangeMiles range in miles
 * @returns {boolean} True if addresses are within rangeMiles of each other
 */
const areAddressesInRange = async (geocoder, addressA, addressB, rangeMiles) => {
  const resultPromise = new Promise((resolve, reject) => {
    geocoder.geocode({ address: addressA }, (resA, statusA) => {
      if (statusA !== window.google.maps.GeocoderStatus.OK) {
        reject(Error(`Geocoding failed for ${addressA}: ${statusA}`));
      }

      geocoder.geocode({ address: addressB }, (resB, statusB) => {
        if (statusB !== window.google.maps.GeocoderStatus.OK) {
          reject(Error(`Geocoding failed for ${addressB}: ${statusB}`));
        }

        const distMeters = window.google.maps.geometry.spherical.computeDistanceBetween(
          resA[0].geometry.location,
          resB[0].geometry.location,
        );
        const distMiles = distMeters / 1609.34;

        resolve(distMiles < rangeMiles);
      });
    });
  });

  try {
    return await resultPromise;
  } catch (e) {
    console.error(`Failed to check addresses in range: ${e}`);
    return false;
  }
};

const calculateGrandTotal = (subTotal, isDelivery) => {
  if (subTotal === 0) {
    return 0;
  }

  const salesTaxHold = Math.floor(subTotal * SALES_TAX_PERCENT);
  return isDelivery
    ? subTotal * (1 + DELIVERY_TIP_PERCENT) + salesTaxHold + DELIVERY_SERVICE_FEE_CENTS
    : subTotal * (1 + DELIVERY_TIP_PERCENT) + SALES_TAX_PERCENT + IN_HOUSE_SERVICE_FEE_CENTS;
};

export {
  parseFormattedAddress,
  areAddressesInRange,
  getAddressLocation,
  areLocationsInRange,
  calculateGrandTotal,
  DELIVERY_RANGE,
  DELIVERY_TIP_PERCENT,
  DELIVERY_SERVICE_FEE_CENTS,
  SALES_TAX_PERCENT,
  IN_HOUSE_SERVICE_FEE_CENTS,
};

export default capitalize;
